import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useEffect, useRef } from 'react';

const createGraphQLClient = ({ endpoint, clientId, apiSecret, }) => {
    const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-client-id": clientId,
        "x-jwt-token": apiSecret,
        "x-api-key": apiSecret,
    };
    const execute = async (query, variables) => {
        const res = await fetch(`${endpoint}/graphql`, {
            method: "POST",
            headers,
            body: JSON.stringify({ query, variables }),
        });
        const { data, errors } = await res.json();
        if (errors?.length)
            throw new Error(errors[0].message);
        return data;
    };
    // 👇 Put your custom queries directly here
    const getPaymentGatewayFees = async (paymentGateway, type) => {
        const QUERY = `
      query ($payment_gateway: String, $type: String) {
        organisationPaymentGatewayByTierFees(payment_gateway: $payment_gateway, type: $type) {
          payment_gateway_fee {
            id
            payment_gateway
            fee_type
            percentage_fee
            fee_type_value
            fixed_fee_base
            fixed_fee_cap
            card_location
            tier_id
          }
          organisation {
            id
            is_oncharge_credit_card_fees
            is_oncharge_payto_fees
            is_oncharge_pay_id_fees
            is_oncharge_visa_domestic_fees
            is_oncharge_visa_international_fees
            is_oncharge_mastercard_domestic_fees
            is_oncharge_mastercard_international_fees
            is_oncharge_amex_fees
            has_manual_payment
          }
        }
      }
    `;
        try {
            const result = await execute(QUERY, {
                payment_gateway: paymentGateway,
                type,
            });
            return result.organisationPaymentGatewayByTierFees;
        }
        catch (error) {
            throw error;
        }
    };
    const getZaiTokenAuth = async (token_type, type, contact) => {
        const QUERY = `
        query zaiContactTokenAuth($type:String!, $token_type: String!, $contact: ContactInput!){
                zaiContactTokenAuth(token_type: $token_type, type: $type, contact: $contact) {
                    token
                    token_type
                    user_id
                }
            }
      `;
        try {
            const result = await execute(QUERY, {
                token_type,
                type,
                contact,
            });
            if (!result?.zaiContactTokenAuth) {
                throw new Error("Failed to get Zai token auth");
            }
            return result.zaiContactTokenAuth;
        }
        catch (error) {
            throw error;
        }
    };
    const processSawfishPayment = async (zai_user_id, zai_account_id, zai_device_id, ip_address, amount, payment_gateway_fee_id, contact, metadata, description) => {
        const MUTATION = `
        mutation payByPluginRequest($input: PayByPluginInput!) {
                payByPluginRequest(input: $input) {
                  type
                  uuid
                  payment_gateway_fee
                  paid_at
                  status
                  total_amount
                  reference
                  transaction_id
                  payment_brand
                  metadata
                  created_at
                  updated_at
                  failed_reason
                }
            }
      `;
        try {
            const result = await execute(MUTATION, {
                input: {
                    zai_user_id,
                    zai_account_id,
                    zai_device_id,
                    ip_address,
                    amount,
                    payment_gateway_fee_id,
                    contact,
                    metadata,
                    description,
                },
            });
            return result?.payByPluginRequest || [];
        }
        catch (error) {
            throw error;
        }
    };
    return {
        execute,
        getPaymentGatewayFees,
        getZaiTokenAuth,
        processSawfishPayment,
    };
};

/**
 * REST API functions for Sawfish authentication
 */
/**
 * Generates an access token that is valid for 10 minutes
 * This endpoint should be called only once. After obtaining the refresh_token,
 * use the refreshToken API to get a new access token.
 *
 * @param options - Configuration options for generating token
 * @returns Promise with token response data
 * @throws Error if the request fails
 */
const generateToken = async (options) => {
    const { endpoint, clientId, apiSecret } = options;
    try {
        const response = await fetch(`${endpoint}/api/v2/accounting/token/generate-token`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "x-client-id": clientId,
                "x-api-key": apiSecret,
            },
        });
        const result = await response.json();
        if (!response.ok || result.status === "ERROR") {
            const errorMessage = result.message || "Failed to generate token";
            throw new Error(errorMessage);
        }
        // console.log("generateToken", result);
        return result.data;
    }
    catch (error) {
        console.error("Error generating token:", error);
        if (error instanceof Error) {
            throw error;
        }
        throw new Error("Unknown error occurred while generating token");
    }
};
/**
 * Refreshes the access token using a refresh token
 * This endpoint should be used to obtain a new access token after the initial token expires.
 *
 * @param options - Configuration options for refreshing token
 * @returns Promise with token response data
 * @throws Error if the request fails
 */
const refreshToken = async (options) => {
    const { endpoint, clientId, apiSecret, refreshToken } = options;
    try {
        const response = await fetch(`${endpoint}/api/v2/accounting/token/refresh-token`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "x-client-id": clientId,
                "x-jwt-token": apiSecret,
                "x-api-key": apiSecret,
            },
            body: JSON.stringify({
                refresh_token: refreshToken,
            }),
        });
        const result = await response.json();
        if (!response.ok || result.status === "ERROR") {
            const errorMessage = result.message || "Failed to refresh token";
            throw new Error(errorMessage);
        }
        // console.log("refreshToken", result);
        return result.data;
    }
    catch (error) {
        console.error("Error refreshing token:", error);
        if (error instanceof Error) {
            throw error;
        }
        throw new Error("Unknown error occurred while refreshing token");
    }
};
/**
 * Revoke an access token
 *
 * @param options - Configuration options for revoking token
 * @returns Promise with token response data
 * @throws Error if the request fails
 */
const revokeToken = async (options) => {
    const { endpoint, clientId, apiSecret } = options;
    try {
        const response = await fetch(`${endpoint}/api/v2/accounting/token/revoke-token`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "x-client-id": clientId,
                "x-api-key": apiSecret,
            },
        });
        const result = await response.json();
        if (!response.ok || result.status === "ERROR") {
            const errorMessage = result.message || "Failed to revoke token";
            throw new Error(errorMessage);
        }
        // console.log("revokeToken", result);
        return result.data;
    }
    catch (error) {
        console.error("Error revoking token:", error);
        if (error instanceof Error) {
            throw error;
        }
        throw new Error("Unknown error occurred while revoking token");
    }
};

var PaymentMethodTypes;
(function (PaymentMethodTypes) {
    PaymentMethodTypes["CREDIT_CARD"] = "credit_card";
    PaymentMethodTypes["PAY_ID"] = "payid";
    PaymentMethodTypes["DIRECT_TRANSFER"] = "direct_transfer";
    PaymentMethodTypes["PAY_TO"] = "paito";
})(PaymentMethodTypes || (PaymentMethodTypes = {}));

const calculateZaiFee = (organisation, amount, paymentGatewayFee) => {
    if (!paymentGatewayFee)
        return 0;
    const { is_oncharge_credit_card_fees, is_oncharge_visa_domestic_fees, is_oncharge_visa_international_fees, is_oncharge_mastercard_domestic_fees, is_oncharge_mastercard_international_fees, is_oncharge_amex_fees, } = organisation;
    const { fee_type, percentage_fee, fixed_fee_base, fixed_fee_cap, fee_type_value, } = paymentGatewayFee;
    let fee = 0;
    switch (fee_type) {
        case PaymentMethodTypes.CREDIT_CARD:
            switch (fee_type_value) {
                case "visa":
                    // Check oncharge for Visa
                    if (!is_oncharge_visa_domestic_fees ||
                        !is_oncharge_visa_international_fees)
                        return 0; // Absorb fee
                    break;
                case "mastercard":
                    // Check oncharge for Mastercard
                    if (!is_oncharge_mastercard_domestic_fees ||
                        !is_oncharge_mastercard_international_fees)
                        return 0; // Absorb fee
                    break;
                case "amex":
                    // Check oncharge for Amex
                    if (!is_oncharge_amex_fees)
                        return 0; // Absorb fee
                    break;
                default:
                    // Check oncharge for default if card not known
                    if (!is_oncharge_credit_card_fees)
                        return 0; // Absorb fee
                    break;
            }
            break;
        case PaymentMethodTypes.PAY_ID:
            if (!organisation?.is_oncharge_pay_id_fees)
                return 0; // Absorb fee
            break;
        case PaymentMethodTypes.PAY_TO:
            if (!organisation?.is_oncharge_payto_fees)
                return 0; // Absorb fee
            break;
    }
    fee = percentage_fee * amount + fixed_fee_base;
    fee = fee * 1.1 || 0; // Add GST - move this below the fixed_fee_cap if GST applied to fixed_fee_cap
    if (fixed_fee_cap && fee > fixed_fee_cap) {
        fee = fixed_fee_cap;
    }
    return fee;
};
const mountHostedFields = async (assembly, env, onCardChanged, setFieldErrors) => {
    const fieldStyles = {
        input: {
            "border-color": "#e5e7eb",
            "border-radius": 8,
            color: "#000245",
            "font-family": "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
            "letter-spacing": "0.02em",
            padding: "1px",
            margin: "0",
            "font-size": "14px",
        },
        ".invalid": {
            color: "#cc0001",
        },
        "::placeholder": {
            color: "#757575",
        },
        ".invalid .card-icon": {
            color: "#cc0001",
        },
    };
    const instance = assembly.hostedFields({ environment: env });
    const fields = [
        { type: "cardName", placeholder: "Full Name", id: "card-name-field" },
        {
            type: "cardNumber",
            placeholder: "•••• •••• •••• ••••",
            id: "card-number-field",
        },
        { type: "cardExpiry", placeholder: "MM/YY", id: "card-expiry-field" },
        { type: "cardCvv", placeholder: "•••", id: "card-cvv-field" },
    ];
    for (const { type, placeholder, id } of fields) {
        const field = instance.create(type, { placeholder, styles: fieldStyles });
        field.mount(`#${id}`);
        field.on("change", (event) => {
            if (event?.cardBrand)
                onCardChanged({ company: event.cardBrand });
            setFieldErrors((p) => ({
                ...p,
                [event.fieldType]: event.error?.message || "",
            }));
        });
    }
    return instance;
};
const removeIframes = async () => {
    const allIframes = Array.from(document.getElementsByTagName("iframe"));
    allIframes.forEach((iframe) => {
        if (iframe?.name?.startsWith("hosted-field-")) {
            if (iframe.parentNode)
                iframe.parentNode.removeChild(iframe);
        }
    });
};
const installZaiScript = () => {
    if (document.getElementById("zai-hf-script") || window.assembly)
        return;
    // Check if script is already loading
    if (window.assembly)
        return;
    const zaiScript = document.createElement("script");
    zaiScript.setAttribute("src", "https://hosted-fields.assemblypay.com/assembly.js");
    zaiScript.setAttribute("id", "zai-hf-script");
    document.head.appendChild(zaiScript);
};
const formatMoney = (value) => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(value);
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".spinner {\n    border: 6px solid #f3f3f3;\n    border-top: 6px solid #3498db;\n    border-radius: 50%;\n    width: 80px;\n    height: 80px;\n    animation: spin 2s linear infinite;\n}\n.spinner-container {\n    display: flex;\n    justify-content: center;\n    align-items: center;\n}\n\n@keyframes spin {\n    0% {\n        transform: rotate(0deg);\n    }\n    100% {\n        transform: rotate(360deg);\n    }\n}\n";
styleInject(css_248z);

function PageLoadingSpinner(props) {
    const { ...rest } = props;
    return (jsx("div", { className: "spinner-container w-full h-full", ...rest, children: jsx("div", { className: "spinner" }) }));
}

const ZaiCreditCardForm = ({ title, submitButtonText, graphQLClient, contact, zaiEnv = "pre-live", amount, type, customStyles = {}, onProcessPayment, onCardChanged, onConfirm, }) => {
    const [_fieldErrors, setFieldErrors] = useState({
        cardNumber: "",
        cardName: "",
        cardExpiry: "",
        cardCvv: "",
    });
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [token, setToken] = useState(null);
    const [zaiScriptError, setZaiScriptError] = useState(false);
    const [userId, setUserId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hostedFields, setHostedFields] = useState(null);
    var isInit = false;
    // Merge custom styles with default styles
    const styles = {
        formContainer: {
            ...defaultStyles.formContainer,
            ...customStyles.formContainer,
        },
        errorContainer: {
            ...defaultStyles.errorContainer,
            ...customStyles.errorContainer,
        },
        errorText: { ...defaultStyles.errorText, ...customStyles.errorText },
        alertContainer: {
            ...defaultStyles.alertContainer,
            ...customStyles.alertContainer,
        },
        alertText: { ...defaultStyles.alertText, ...customStyles.alertText },
        form: { ...defaultStyles.form, ...customStyles.form },
        formTitle: { ...defaultStyles.formTitle, ...customStyles.formTitle },
        fieldContainer: {
            ...customStyles.fieldContainer,
        },
        fieldLabel: { ...defaultStyles.fieldLabel, ...customStyles.fieldLabel },
        fieldInput: { ...customStyles.fieldInput },
        fieldErrorContainer: {
            ...customStyles.fieldErrorContainer,
        },
        fieldErrorText: {
            ...customStyles.fieldErrorText,
        },
        fieldRow: { ...defaultStyles.fieldRow, ...customStyles.fieldRow },
        fieldHalf: { ...customStyles.fieldHalf },
        amountContainer: {
            ...defaultStyles.amountContainer,
            ...customStyles.amountContainer,
        },
        amountText: { ...defaultStyles.amountText, ...customStyles.amountText },
        submitButton: {
            ...defaultStyles.submitButton,
            ...customStyles.submitButton,
        },
        submitButtonDisabled: {
            ...defaultStyles.submitButtonDisabled,
            ...customStyles.submitButtonDisabled,
        },
        submitButtonText: {
            ...defaultStyles.submitButtonText,
            ...customStyles.submitButtonText,
        },
        footerText: { ...defaultStyles.footerText, ...customStyles.footerText },
        hostedField: { ...defaultStyles.hostedField, ...customStyles.hostedField },
    };
    useEffect(() => {
        load();
        return () => {
            cleanup();
        };
    }, []);
    const load = async () => {
        if (isInit)
            return; // Very bad way of avoiding parent reload
        isInit = true; // Very bad way of avoiding parent reload
        try {
            setIsLoading(true);
            setZaiScriptError(false);
            installZaiScript();
            removeIframes();
            const res = await graphQLClient.getZaiTokenAuth("card", type, contact);
            if (!res?.token)
                throw new Error("Missing Sawfish auth token");
            setToken(res.token);
            setUserId(res.user_id);
            // Wait for assembly SDK
            let retries = 0;
            while (!window.assembly?.hostedFields && retries < 200) {
                await new Promise((r) => setTimeout(r, 50));
                retries++;
            }
            if (!window.assembly?.hostedFields)
                throw new Error("Sawfish SDK failed to initialise hosted fields");
            const assembly = window.assembly;
            const hostedFieldsInstance = await mountHostedFields(assembly, zaiEnv, onCardChanged, setFieldErrors);
            setHostedFields(hostedFieldsInstance);
        }
        catch (err) {
            console.error("Zai init error:", err);
            setZaiScriptError(true);
        }
        finally {
            setIsLoading(false);
            setIsSubmitting(false);
        }
    };
    const cleanup = async () => {
        if (hostedFields) {
            try {
                hostedFields.destroy();
            }
            catch (error) {
                console.warn("Error destroying hosted fields:", error);
            }
        }
    };
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError("");
        if (onConfirm) {
            const shouldProceed = await onConfirm();
            if (!shouldProceed) {
                setIsSubmitting(false);
                return;
            }
        }
        try {
            const response = await hostedFields.createCardAccount({
                token,
                user_id: userId,
            });
            const deviceId = await hostedFields?.captureDeviceId();
            const ipAddress = await window.assembly.getIPAddress();
            onProcessPayment({
                card: response?.card_accounts,
                userId: userId ?? "",
                deviceId,
                ipAddress,
                amount: amount?.value,
            });
        }
        catch (err) {
            if (err.errors) {
                setError(Object.values(err.errors)[0]);
            }
            else {
                setError("There was an error processing your credit card.");
            }
        }
        finally {
            setIsSubmitting(false);
        }
    };
    if (zaiScriptError)
        return (jsx("div", { style: styles.alertContainer, children: jsx("div", { style: styles.alertText, children: "There was an error loading the payment form. Please try again later." }) }));
    return (jsxs("div", { style: styles.formContainer, children: [error && (jsx("div", { style: styles.errorContainer, children: jsx("div", { style: styles.errorText, children: error }) })), isLoading && jsx(PageLoadingSpinner, {}), jsxs("div", { style: styles.form, hidden: isLoading, children: [jsxs("form", { onSubmit: (e) => {
                            e.preventDefault();
                            handleSubmit();
                        }, children: [typeof title === "string" || title === undefined ? (jsx("div", { style: styles.fieldRow, children: jsx("div", { style: styles.formTitle, children: title || "Enter card details" }) })) : (title), jsxs("div", { style: styles.fieldRow, children: [jsx("div", { style: styles.fieldLabel, children: "Card Holder Name" }), jsx("div", { id: "card-name-field", style: styles.hostedField })] }), jsxs("div", { style: styles.fieldRow, children: [jsx("div", { style: styles.fieldLabel, children: "Card Number" }), jsx("div", { id: "card-number-field", style: styles.hostedField })] }), jsxs("div", { style: styles.fieldRow, children: [jsx("div", { style: styles.fieldLabel, children: "Expiry Date" }), jsx("div", { id: "card-expiry-field", style: styles.hostedField })] }), jsxs("div", { style: styles.fieldRow, children: [jsx("div", { style: styles.fieldLabel, children: "CVV" }), jsx("div", { id: "card-cvv-field", style: styles.hostedField })] }), amount?.total !== undefined && (jsx("div", { style: styles.amountContainer, children: jsxs("div", { style: styles.amountText, children: ["Amount to pay: ", formatMoney(amount.total)] }) })), jsx("button", { type: "submit", disabled: isSubmitting || isLoading, style: {
                                    ...styles.submitButton,
                                    ...(isSubmitting || isLoading ? styles.submitButtonDisabled : {}),
                                }, children: jsx("div", { style: styles.submitButtonText, children: isSubmitting || isLoading
                                        ? "Processing..."
                                        : amount?.total !== undefined
                                            ? submitButtonText || "Confirm Payment"
                                            : submitButtonText || "Submit" }) })] }), jsx("div", { style: styles.footerText, children: "Payment securely processed by Assembly Payments." })] })] }));
};
const defaultStyles = {
    formContainer: {
        maxWidth: 460,
        margin: "0 auto",
        padding: "16px 24px",
    },
    errorContainer: {
        backgroundColor: "#fee2e2",
        padding: 16,
        borderRadius: 8,
        marginBottom: 16,
    },
    errorText: {
        color: "#b91c1c",
        textAlign: "center",
        fontSize: 14,
    },
    alertContainer: {
        backgroundColor: "#fee2e2",
        padding: 16,
        borderRadius: 8,
        marginBottom: 16,
    },
    alertText: {
        color: "#b91c1c",
        textAlign: "center",
        fontSize: 14,
    },
    form: {
        borderRadius: 8,
        padding: "0 8px",
    },
    formTitle: {
        fontWeight: "600",
        marginBottom: 20,
        fontSize: 18,
        color: "#111827",
    },
    fieldLabel: {
        marginBottom: 8,
        fontSize: 14,
        fontWeight: "500",
        color: "#374151",
        display: "block",
    },
    fieldRow: {
        display: "flex",
        flexDirection: "column",
        marginBottom: 16,
    },
    amountContainer: {
        padding: 16,
        marginTop: 16,
        backgroundColor: "#f3f4f6",
        marginBottom: 16,
        borderRadius: 8,
        textAlign: "center",
    },
    amountText: {
        fontWeight: "bold",
        fontSize: 14,
        alignSelf: "center",
        color: "#374151",
    },
    submitButton: {
        backgroundColor: "#3b82f6",
        paddingTop: 14,
        paddingBottom: 14,
        paddingLeft: 24,
        paddingRight: 24,
        borderRadius: 8,
        border: "none",
        cursor: "pointer",
        width: "100%",
        fontSize: 16,
        fontWeight: "600",
        transition: "background-color 0.2s ease",
    },
    submitButtonDisabled: {
        opacity: 0.5,
    },
    submitButtonText: {
        color: "#ffffff",
        fontSize: 16,
        fontWeight: "600",
        textAlign: "center",
    },
    footerText: {
        fontSize: 12,
        color: "#6b7280",
        textAlign: "center",
        marginTop: 16,
    },
    hostedField: {
        fontSize: 14,
        height: 44,
        padding: "12px 16px",
        border: "1px solid #d1d5db",
        backgroundColor: "#ffffff",
        borderRadius: 8,
        width: "100%",
        boxSizing: "border-box",
        transition: "border-color 0.2s ease",
    },
};

const PaymentMethodRow = ({ isHeadingHidden = false, paymentMessage, children, }) => {
    return (jsxs("div", { className: "flex flex-col w-full", children: [!isHeadingHidden && jsx("h2", { className: "text-[20px]", children: "Payment Methods" }), paymentMessage && jsx("div", { className: "mt-4", children: paymentMessage }), jsx("div", { className: "mt-4", children: children })] }));
};

const PaymentForm = ({ title, submitButtonText, environment, endpoint, apiSecret, clientId, mode = "payment", amount, contact, metadata, description, customStyles, onConfirm, onPaymentSuccess, onPaymentError, onCardAccountCreated, }) => {
    const clientRef = useRef(null);
    const [fees, setFees] = useState([]);
    const [paymentGatewayFee, setPaymentGatewayFee] = useState();
    const [organisation, setOrganisation] = useState(null);
    const [refreshToken$1, setRefreshToken] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState();
    useEffect(() => {
        init();
    }, []);
    const init = async () => {
        await revokeToken({
            endpoint,
            clientId,
            apiSecret,
        });
        const tokenData = await generateToken({
            endpoint,
            clientId,
            apiSecret,
        });
        updateClient(tokenData);
        fetchFees();
    };
    const updateClient = async (tokenData) => {
        setRefreshToken(tokenData.refresh_token);
        clientRef.current = createGraphQLClient({
            endpoint,
            clientId,
            apiSecret: tokenData?.token,
        });
    };
    const fetchFees = async () => {
        try {
            const client = clientRef.current;
            if (!client)
                throw new Error("Missing Sawfish Client");
            const res = await client.getPaymentGatewayFees("zai", "direct");
            setFees(res.payment_gateway_fee);
            setOrganisation(res.organisation);
            setIsLoading(false);
        }
        catch (error) {
            console.error("fetchFees ->", error);
        }
    };
    const processPayment = async ({ card, user_id, device_id, ip_address, amount, }) => {
        setIsLoading(true);
        const tokenData = await refreshToken({
            endpoint,
            clientId,
            apiSecret,
            refreshToken: refreshToken$1,
        });
        updateClient(tokenData);
        const client = clientRef.current;
        if (!client)
            throw new Error("Missing Sawfish Client");
        try {
            // If mode is card-retrieval, just return the card account details
            if (mode === "card-retrieval") {
                onCardAccountCreated?.(card);
                setIsLoading(false);
                return;
            }
            // Otherwise, process the payment
            const res = await client.processSawfishPayment(user_id, card.id, device_id, ip_address, amount, paymentGatewayFee?.id, contact, metadata, description);
            if (mode === "payment-card-retrieval") {
                onPaymentSuccess?.({ ...res, card: card });
            }
            else {
                onPaymentSuccess?.(res);
            }
        }
        catch (error) {
            onPaymentError?.(error);
            setError(error);
        }
        finally {
            setIsLoading(false);
            setTimeout(() => {
                setError("");
            }, 10000);
        }
    };
    const onCardCompanyChange = (company) => {
        var fee = fees.filter((f) => f.fee_type_value == company && f.card_location == "domestic");
        // If card is not recognised apply the high fee rate
        if (fee.length == 0) {
            fee = fees.filter((f) => f.fee_type_value == "unknown" && f.card_location == "domestic");
        }
        setPaymentGatewayFee(fee[0]);
    };
    if (isLoading)
        return jsx(PageLoadingSpinner, {});
    return (jsx("div", { className: "w-[100%]", children: jsx("div", { className: "col-span-full lg:col-span-9 pt-3 lg:pt-36 pb-10 px-3 lg:pl-[120px]", children: jsx(PaymentMethodRow, { isHeadingHidden: !!title, children: jsx("div", { className: "space-y-4", children: jsxs("div", { className: "border-b max-w", children: [error && (jsx("div", { style: styles.errorContainer, children: jsx("div", { style: styles.errorText, children: error }) })), jsx(ZaiCreditCardForm, { title: title, submitButtonText: submitButtonText, customStyles: customStyles, onConfirm: onConfirm, zaiEnv: environment === "production" ? "production" : "pre-live", contact: contact, graphQLClient: clientRef.current, type: "plugin", amount: (mode === "payment" || mode === "payment-card-retrieval") &&
                                    amount !== undefined
                                    ? {
                                        value: amount,
                                        total: amount +
                                            calculateZaiFee(organisation, amount, paymentGatewayFee),
                                    }
                                    : undefined, onProcessPayment: (e) => {
                                    processPayment({
                                        ...e,
                                        user_id: e.userId,
                                        device_id: e.deviceId,
                                        ip_address: e.ipAddress,
                                    });
                                }, onCardChanged: (v) => onCardCompanyChange(v.company) }, 0)] }) }) }) }) }));
};
const styles = {
    errorContainer: {
        maxWidth: 460,
        backgroundColor: "#fee2e2",
        padding: 16,
        borderRadius: 8,
        marginBottom: 16,
        marginLeft: "auto",
        marginRight: "auto",
    },
    errorText: {
        color: "#b91c1c",
        textAlign: "center",
        fontSize: 14,
    },
};

export { PaymentForm };
//# sourceMappingURL=index.js.map
